import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const CategoryContext = createContext();

export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};

const defaultCategories = [
  { id: '1', name: 'Veg' },
  { id: '2', name: 'NonVeg' }
];

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState(defaultCategories);

  // Use environment variable for API URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    const stored = localStorage.getItem('categories');
    if (stored) {
      const arr = JSON.parse(stored);
      if (!arr || arr.length === 0) {
        setCategories(defaultCategories);
        localStorage.setItem('categories', JSON.stringify(defaultCategories));
      } else {
        setCategories(arr);
      }
    } else {
      setCategories(defaultCategories);
      localStorage.setItem('categories', JSON.stringify(defaultCategories));
    }
  }, []);

  useEffect(() => {
    // Always ensure at least Veg and NonVeg exist
    if (!categories || categories.length === 0) {
      setCategories(defaultCategories);
      localStorage.setItem('categories', JSON.stringify(defaultCategories));
    } else {
      localStorage.setItem('categories', JSON.stringify(categories));
    }
  }, [categories]);

  const addCategory = (name) => {
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase())) {
      toast.error('Category already exists');
      return { success: false };
    }
    const newCat = { id: Date.now().toString(), name };
    setCategories([...categories, newCat]);
    toast.success('Category added');
    return { success: true };
  };

  const editCategory = (id, name) => {
    if (categories.some(c => c.name.toLowerCase() === name.toLowerCase() && c.id !== id)) {
      toast.error('Category already exists');
      return { success: false };
    }
    setCategories(categories.map(c => c.id === id ? { ...c, name } : c));
    toast.success('Category updated');
    return { success: true };
  };

  const deleteCategory = (id) => {
    const protectedNames = ['Veg', 'NonVeg'];
    const cat = categories.find(c => c.id === id);
    if (cat && protectedNames.includes(cat.name)) {
      toast.error('Cannot delete default category');
      return;
    }
    setCategories(categories.filter(c => c.id !== id));
    toast.success('Category deleted');
  };

  const value = {
    categories,
    addCategory,
    editCategory,
    deleteCategory
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
}; 